import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface UserAvatarProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showFallback?: boolean;
}

const sizeClasses = {
  sm: 'h-7 w-7 text-sm',
  md: 'h-9 w-9 text-base',
  lg: 'h-14 w-14 text-xl',
};

const emojiSizes = {
  sm: 'text-base',
  md: 'text-xl',
  lg: 'text-3xl',
};

export function UserAvatar({ size = 'md', className, showFallback = true }: UserAvatarProps) {
  const { user } = useAuth();
  const { profile, loading } = useUserProfile();

  // Get user initials for fallback
  const getUserInitials = () => {
    // First try display_name
    if (profile?.display_name) {
      return profile.display_name.charAt(0).toUpperCase();
    }
    // Then try email
    if (user?.email) {
      const name = user.email.split('@')[0];
      return name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Get avatar emoji from profile or localStorage
  const avatarEmoji = profile?.avatar || localStorage.getItem('user_avatar');
  const themeColor = profile?.theme_color || localStorage.getItem('user_color');

  // If we have an emoji avatar, show it
  if (avatarEmoji) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-full border-2 transition-all shadow-sm",
          sizeClasses[size],
          className
        )}
        style={{
          backgroundColor: themeColor ? `${themeColor}30` : 'hsl(var(--primary) / 0.15)',
          borderColor: themeColor || 'hsl(var(--primary))',
        }}
      >
        <span className={emojiSizes[size]}>
          {avatarEmoji}
        </span>
      </div>
    );
  }

  // Fallback to initials
  if (!showFallback) return null;

  return (
    <Avatar 
      className={cn(
        sizeClasses[size], 
        "border-2 border-primary shadow-sm transition-all",
        className
      )}
    >
      <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
        {getUserInitials()}
      </AvatarFallback>
    </Avatar>
  );
}
